import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { PromptsManagementService } from '../../../../services'
import styles from './styles.module.css'

const SUCCESS_TOAST_STYLE = {
  background: '#16a34a',
  color: '#ffffff',
}

const ERROR_TOAST_STYLE = {
  background: '#dc2626',
  color: '#ffffff',
}

const isProduction = import.meta.env.VITE_ENV === 'prod' || import.meta.env.VITE_ENV === 'dev'
const isLocalDev = import.meta.env.VITE_ENV === 'dev'

function PromptManagerList() {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({
    key: 'updatedAt',
    direction: 'desc',
  })
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSyncingAllFromHomolog, setIsSyncingAllFromHomolog] = useState(false)
  const [syncStatus, setSyncStatus] = useState({})

  useEffect(() => {
    const loadPrompts = async () => {
      setIsLoading(true)
      setError('')
      const response = await PromptsManagementService.getAllPrompts()

      if (response?.responseStatus === 200 && Array.isArray(response?.data)) {
        setRows(response.data)

        if (isProduction) {
          const statusResponse = await PromptsManagementService.getSyncStatus()
          if (statusResponse?.responseStatus === 200) {
            setSyncStatus(statusResponse.data || {})
          }
        }
      } else {
        setError('Não foi possível carregar a lista de prompts.')
      }
      setIsLoading(false)
    }

    loadPrompts()
  }, [])

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        }
      }

      return {
        key,
        direction: 'asc',
      }
    })
  }

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  const sortedRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const filteredRows = !normalizedSearch
      ? rows
      : rows.filter((item) =>
        String(item?.name || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )

    const clonedRows = [...filteredRows]
    const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1

    clonedRows.sort((a, b) => {
      if (sortConfig.key === 'name') {
        const valueA = (a.name || '').toLowerCase()
        const valueB = (b.name || '').toLowerCase()
        return valueA.localeCompare(valueB) * directionMultiplier
      }

      if (sortConfig.key === 'schema') {
        const valueA = a.schema ? 1 : 0
        const valueB = b.schema ? 1 : 0
        return (valueA - valueB) * directionMultiplier
      }

      if (sortConfig.key === 'updatedAt') {
        const valueA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const valueB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        return (valueA - valueB) * directionMultiplier
      }

      return 0
    })

    return clonedRows
  }, [rows, sortConfig, searchTerm])

  const allSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedIds.has(row.uuid))
  const someSelected =
    sortedRows.some((row) => selectedIds.has(row.uuid)) && !allSelected

  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds)
      sortedRows.forEach((row) => next.delete(row.uuid))
      setSelectedIds(next)
    } else {
      const next = new Set(selectedIds)
      sortedRows.forEach((row) => next.add(row.uuid))
      setSelectedIds(next)
    }
  }

  const toggleSelectRow = (uuid) => {
    const next = new Set(selectedIds)
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    setSelectedIds(next)
  }

  const handleUpdate = async () => {
    if (selectedIds.size === 0) return

    const selectedNames = rows
      .filter((row) => selectedIds.has(row.uuid))
      .map((row) => row.name)

    setIsSyncing(true)
    const response = await PromptsManagementService.syncPrompts(selectedNames)
    setIsSyncing(false)

    if (response?.responseStatus === 200) {
      const { synced, failed } = response.data || {}
      const syncedCount = synced?.length || 0
      const failedCount = failed?.length || 0

      if (syncedCount > 0) {
        toast.success(`${syncedCount} prompt(s) sincronizado(s) com sucesso.`, {
          style: SUCCESS_TOAST_STYLE,
        })
      }

      if (failedCount > 0) {
        const failedNames = failed.map((f) => f.name).join(', ')
        toast.error(
          `Falha ao sincronizar: ${failedNames}. Motivos: ${failed.map((f) => f.reason).join('; ')}`,
          {
            style: ERROR_TOAST_STYLE,
          },
        )
      }

      // Recarrega lista e status para refletir dados atualizados
      setIsLoading(true)
      const refresh = await PromptsManagementService.getAllPrompts()
      if (refresh?.responseStatus === 200 && Array.isArray(refresh?.data)) {
        setRows(refresh.data)
      }
      if (isProduction) {
        const statusResponse = await PromptsManagementService.getSyncStatus()
        if (statusResponse?.responseStatus === 200) {
          setSyncStatus(statusResponse.data || {})
        }
      }
      setIsLoading(false)
      setSelectedIds(new Set())
      return
    }

    const backendMessage =
      response?.response?.data?.message || 'Erro desconhecido ao sincronizar.'
    toast.error(`Não foi possível sincronizar: ${backendMessage}`, {
      style: ERROR_TOAST_STYLE,
    })
  }

  const handleSyncAllFromHomolog = async () => {
    const confirmed = window.confirm(
      'Isso vai apagar todos os prompts locais e substituir pelos de homolog. Deseja continuar?',
    )
    if (!confirmed) return

    setIsSyncingAllFromHomolog(true)
    const response = await PromptsManagementService.syncAllFromHomolog()
    setIsSyncingAllFromHomolog(false)

    if (response?.responseStatus === 200) {
      const { promptCount, commitCount } = response.data || {}
      toast.success(
        `Banco local sincronizado com homolog: ${promptCount ?? 0} prompt(s), ${commitCount ?? 0} commit(s).`,
        { style: SUCCESS_TOAST_STYLE },
      )

      setIsLoading(true)
      const refresh = await PromptsManagementService.getAllPrompts()
      if (refresh?.responseStatus === 200 && Array.isArray(refresh?.data)) {
        setRows(refresh.data)
      }
      setIsLoading(false)
      setSelectedIds(new Set())
      return
    }

    const backendMessage =
      response?.response?.data?.message ||
      response?.response?.data?.error?.message ||
      'Erro desconhecido ao sincronizar com homolog.'
    toast.error(`Não foi possível trazer de homolog: ${backendMessage}`, {
      style: ERROR_TOAST_STYLE,
    })
  }

  if (isLoading) return <p className={styles.stateText}>Carregando prompts...</p>
  if (error) return <p className={styles.stateTextError}>{error}</p>

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Prompt Manager List</h2>
        <div className={styles.headerActions}>
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className={styles.searchInput}
              placeholder="Buscar por nome..."
            />
          </div>
          <span className={styles.badge}>Total: {sortedRows.length}</span>
          {isProduction && (
            <button
              type="button"
              className={styles.updateButton}
              onClick={handleUpdate}
              disabled={selectedIds.size === 0 || isSyncing}
            >
              {isSyncing
                ? 'Sincronizando...'
                : `Atualizar Selecionados (${selectedIds.size})`}
            </button>
          )}
          {isLocalDev && (
            <button
              type="button"
              className={styles.syncHomologButton}
              onClick={handleSyncAllFromHomolog}
              disabled={isSyncingAllFromHomolog}
            >
              {isSyncingAllFromHomolog
                ? 'Trazendo de homolog...'
                : 'Trazer tudo de homolog'}
            </button>
          )}
          <Link to="/PromptManagerCreate" className={styles.createButton}>
            Criar Novo Prompt
          </Link>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected
                  }}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>
                <button
                  type="button"
                  className={styles.sortButton}
                  onClick={() => handleSort('name')}
                >
                  Nome {getSortIndicator('name')}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={styles.sortButton}
                  onClick={() => handleSort('schema')}
                >
                  Schema {getSortIndicator('schema')}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={styles.sortButton}
                  onClick={() => handleSort('updatedAt')}
                >
                  Atualizado em {getSortIndicator('updatedAt')}
                </button>
              </th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((item) => {
              const isSelected = selectedIds.has(item.uuid)
              const isOutdated = syncStatus[item.name] === true
              return (
                <tr
                  key={item.uuid}
                  className={`${isSelected ? styles.rowSelected : ''} ${isOutdated ? styles.rowOutdated : ''}`}
                >
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectRow(item.uuid)}
                    />
                  </td>
                  <td>
                    <span className={styles.nameCell}>
                      {item.name || '-'}
                      {isOutdated && (
                        <span
                          className={styles.outdatedBadge}
                          title="Prompt desatualizado em relação à homologação"
                        >
                          ●
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{item.schema ? 'Sim' : 'Não'}</td>
                  <td>
                    {item.updatedAt
                      ? new Date(item.updatedAt).toLocaleString('pt-BR')
                      : '-'}
                  </td>
                  <td>
                    <Link
                      to={`/PromptManagerEdit/${item.uuid}`}
                      state={{ promptItem: item }}
                      className={styles.actionButton}
                      title="Editar prompt"
                      aria-label={`Editar prompt ${item.name || ''}`}
                    >
                      ⚙
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PromptManagerList
